import React from 'react';
import { ArrowLeft, ArrowRight, LifeBuoy, MessageSquareWarning, BookOpen } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';
import { TROUBLESHOOTING_CONTAINERS } from '../components/TroubleshootingPageLayout';

const placeholder = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/ieqf3tcr_FOR%20TROUBLESHOOTING.png';

const topics = [
  {
    id: 'common-errors',
    tagline: 'Error codes & quick fixes',
    title: 'Common Errors',
    description: 'Jump to the most frequently encountered API error codes — what they mean, why they fire and the fastest way to resolve each one.',
    accent: 'from-rose-600/15 to-orange-500/10',
  },
  {
    id: 'webhook-delivery',
    tagline: 'Webhook reliability',
    title: 'Webhook Delivery',
    description: 'Debug notifications that aren\'t landing — firewall / WAF rules, IP whitelisting, HTTPS certificates, redelivery behaviour and retry budgets.',
    accent: 'from-sky-600/15 to-blue-500/10',
  },
  {
    id: 'integration-issues',
    tagline: 'Integration pitfalls',
    title: 'Integration Issues',
    description: 'Solve the handful of subtle problems that trip up 90% of first integrations — timezone handling, amount decimals, currency codes, UDF parsing.',
    accent: 'from-amber-600/15 to-yellow-500/10',
  },
];

const Troubleshooting = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col" data-testid="troubleshooting-page">
      <Header />
      <div className="h-20" />

      {/* Hero with full-page image */}
      <section
        className="relative flex-1 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${placeholder})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1929]/88 via-[#0a1929]/80 to-[#163a5f]/85" />

        <div className="relative z-10 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-14">
          <button
            onClick={() => navigate('/technical-documentation')}
            className="flex items-center gap-2 text-white/85 hover:text-white font-medium mb-8 transition-colors duration-200"
            data-testid="troubleshooting-back-button"
          >
            <ArrowLeft size={18} />
            Back to Technical Documentation
          </button>

          <div className="mb-10 max-w-4xl">
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ef5b25]/20 border border-[#ef5b25]/40 text-[#ffcba6] text-[11px] font-bold uppercase tracking-[0.15em] mb-5"
              data-testid="troubleshooting-eyebrow"
            >
              <LifeBuoy size={13} />
              Fix it fast
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight" data-testid="troubleshooting-title">
              Troubleshooting
            </h1>
            <p className="text-lg text-white font-bold leading-relaxed max-w-3xl">
              Something not behaving the way the docs suggested? You're in the right place. This section is a growing library of answers to the questions our integration team hears most often — error codes, webhook delivery quirks and the subtle pitfalls every integrator runs into once.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10 max-w-5xl">
            <div className="rounded-lg bg-white/10 backdrop-blur-md border border-white/15 p-6" data-testid="ts-how-it-works">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquareWarning size={18} className="text-[#ef5b25]" />
                <h2 className="text-lg font-bold text-black">How to use this section</h2>
              </div>
              <p className="text-sm text-black leading-relaxed">
                Start with the topic closest to your symptom. Each page lists the likely causes in order of frequency, with copy-paste checks, validation snippets and direct links to the relevant API Reference page.
              </p>
            </div>
            <div className="rounded-lg bg-white/10 backdrop-blur-md border border-white/15 p-6" data-testid="ts-still-stuck">
              <div className="flex items-center gap-2 mb-3">
                <BookOpen size={18} className="text-[#ef5b25]" />
                <h2 className="text-lg font-bold text-black">Still stuck?</h2>
              </div>
              <p className="text-sm text-black leading-relaxed">
                Drop us a note at <a href="mailto:csr@ecomm365.com" className="text-gray-500 hover:text-gray-700 underline font-semibold">csr@ecomm365.com</a> with your Vendor ID, Message ID and the raw error response. Our integration team will triage and respond as soon as possible.
              </p>
            </div>
          </div>

          {/* 3 Topic cards — fully clickable, no icon block, no Soon badge */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="troubleshooting-topic-grid">
            {topics.map((t) => (
              <Link
                key={t.id}
                to={`/technical-documentation/troubleshooting/${t.id}`}
                className="group relative overflow-hidden bg-white/95 backdrop-blur-sm rounded-lg shadow-xl p-6 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                data-testid={`troubleshooting-topic-${t.id}`}
              >
                <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${t.accent.replace('/15', '').replace('/10', '')} bg-[#1F4E79]`} />
                <div className={`absolute -top-6 -right-6 w-28 h-28 rounded-full blur-2xl bg-gradient-to-br ${t.accent} opacity-60 pointer-events-none`} />

                <div className="relative">
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] mb-1">{t.tagline}</p>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#1F4E79] transition-colors">{t.title}</h3>
                  <p className="text-sm text-gray-700 leading-relaxed mb-4">{t.description}</p>
                  <span className="inline-flex items-center gap-1 text-[#1F4E79] font-semibold text-xs group-hover:gap-2 transition-all">
                    Open guide
                    <ArrowRight size={13} />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          <p className="text-xs text-white/55 mt-8 max-w-3xl" data-testid="troubleshooting-tip">
            <strong className="text-white/75">Tip:</strong> press{' '}
            <kbd className="px-1.5 py-0.5 bg-white/15 border border-white/25 rounded text-[10px] text-white font-sans">⌘</kbd>/
            <kbd className="px-1.5 py-0.5 bg-white/15 border border-white/25 rounded text-[10px] text-white font-sans">Ctrl</kbd>+
            <kbd className="px-1.5 py-0.5 bg-white/15 border border-white/25 rounded text-[10px] text-white font-sans">K</kbd>{' '}
            on any docs page to fuzzy-search error codes (e.g. "0057", "3D Secure failed", "invalid amount") and jump straight to the answer.{' '}
            <Link to="/technical-documentation/appendix/error-codes" className="text-white/85 hover:text-white underline decoration-white/30 hover:decoration-white/70 decoration-1 underline-offset-2 font-semibold">
              Or browse the full error-code table
            </Link>
            <ArrowRight size={12} className="inline ml-1" />
          </p>

          {/* Index of troubleshooting pages */}
          <div className="hidden">
            {TROUBLESHOOTING_CONTAINERS.map((c) => c.title).join(',')}
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default Troubleshooting;
